import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["autoWidth", "className", "rowClassName", "summary", "caption", "ariaCaption", "liveText", "columns", "data", "rowKey", "headerComponent", "headerComponentProps", "bodyComponent", "bodyComponentProps", "bodyRowComponent", "bodyRowComponentProps", "footerComponent", "footerComponentProps", "footerRowComponent", "footerRowComponentProps", "maxHeight", "onCollapseColumns", "columnsCollapseOrder", "customStrings", "paginatedData", "footerRows", "selectedRows", "selectableTypeRow", "tableId", "nonStickyHeader"],
      _excluded2 = ["key"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import { KEYBOARD_KEYS } from "../../common/constants";
import { useUniqueId } from "../../common/hooks";
import { resultOf } from "../../common/utilities";
import EmptyState from "../../EmptyState/EmptyState";
import { ROW_KEY_ATTRIBUTE_NAME, TABLE_BODY_ROW_CLASS } from "./constants";
import extractTheme from "./extractTheme";
import STRINGS from "./strings";
import TableBodyCell from "./TableBodyCell";
import TableCaption, { TableARIAChangeAnnouncer } from "./TableCaption";
import TableFooterCell from "./TableFooterCell";
import TableHeaderCell from "./TableHeaderCell";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { createElement as _createElement } from "@emotion/react";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

const defaultGetRowKey = key => item => item[key];
/**
 * The BaseTable component contains the minimal table functionality.
 * It is intended to be a presentational dummy component which exposes a rich set of props to be extended,
 * in order to provide additional features. The data management, such as sorting,
 * filtering, searching, is left to the user.
 */


const BaseTable = /*#__PURE__*/React.forwardRef((_ref, tableNodeRef) => {
  let {
    autoWidth,
    className,
    rowClassName,
    summary,
    caption,
    ariaCaption,
    liveText,
    // eslint-disable-line react/prop-types
    columns,
    data,
    rowKey,
    headerComponent: THead,
    headerComponentProps,
    bodyComponent: TBody,
    bodyComponentProps,
    bodyRowComponent: TBodyRow,
    bodyRowComponentProps,
    footerComponent: TFooter,
    footerComponentProps,
    footerRowComponent: TFooterRow,
    footerRowComponentProps,
    // eslint-disable-line react/prop-types
    maxHeight,
    onCollapseColumns,
    // eslint-disable-line no-unused-vars
    columnsCollapseOrder,
    // eslint-disable-line no-unused-vars, react/prop-types
    customStrings,
    paginatedData,
    // eslint-disable-line no-unused-vars
    footerRows,
    selectedRows,
    // eslint-disable-line no-unused-vars, react/prop-types
    selectableTypeRow,
    // eslint-disable-line react/prop-types
    tableId,
    // eslint-disable-line no-unused-vars, react/prop-types
    nonStickyHeader
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const internalRef = useRef(null);
  const uniqueId = useUniqueId();
  const {
    tableRoot
  } = extractTheme(_objectSpread({
    theme,
    autoWidth,
    maxHeight
  }, otherProps));
  const emptyDataMessage = (customStrings === null || customStrings === void 0 ? void 0 : customStrings.emptyDataMessage) ?? " ";
  const [focusCellId, setFocusCellId] = useState(`BaseTableCell00${uniqueId}`);
  const getRowKey = typeof rowKey === "string" ? defaultGetRowKey(rowKey) : rowKey;
  const tableData = paginatedData.length ? paginatedData : data;
  const instructions = STRINGS.selectTypeRowInstructions;
  const selectDescription = caption ? `${caption}. ${instructions}` : instructions;
  const selectDescriptionID = useUniqueId(`select-type-row-description`);
  /**
   * @description This method returns props for table cell.
   * @param {Boolean} selectableTypeRow True if `selectableType=row`.
   * @param {Number} columnIndex The value of column index.
   * @param {Number} itemIndex The value of item index.
   * @return {Object} table cell props.
   */

  const getTableBodyCellProps = (selectableTypeRow, columnIndex, itemIndex) => {
    const cellId = `BaseTableCell${columnIndex}${itemIndex}${uniqueId}`;
    const tableBodyCellOtherProps = {
      tabIndex: null,
      id: cellId
    };

    if (selectableTypeRow) {
      if (focusCellId === cellId) {
        tableBodyCellOtherProps.tabIndex = 0;
      } else {
        tableBodyCellOtherProps.tabIndex = -1;
      }
    }

    return tableBodyCellOtherProps;
  };
  /**
   * @description To update the tabindex and focus on cell by updating `focusCellId` state.
   * @param {String} focusCellId to focus the table cell.
   */


  const updateFocusCell = focusCellId => {
    setFocusCellId(focusCellId);
    (tableNodeRef || internalRef).current.querySelector(`#${focusCellId}`).focus();
  };
  /**
   * @description Handles the click event on the table cell.
   * And sets the `focusCellId` as `id` of that cell.
   * @param {EventFacade} The keydown event facade.
   */


  const handleClick = event => {
    const {
      target
    } = event;
    const focusCellId = target.id;
    focusCellId && updateFocusCell(focusCellId);
  };
  /**
   * @description Handles the keydown event on the table cell.
   * And sets the `focusCellId` based on key pressed.
   * @param {EventFacade} The keydown event facade.
   */


  const handleKeyDown = event => {
    const {
      DOWN,
      LEFT,
      RIGHT,
      UP
    } = KEYBOARD_KEYS;
    const {
      target,
      key,
      shiftKey,
      ctrlKey
    } = event;
    const trRow = target.parentElement;
    let focusCellId;

    if (key === UP && !(shiftKey || ctrlKey)) {
      event.preventDefault();
      let row = trRow.previousElementSibling;

      while (row && !row.classList.contains(TABLE_BODY_ROW_CLASS)) {
        row = row.previousElementSibling;
      }

      focusCellId = row && row.children[target.cellIndex].id;
    } else if (key === DOWN && !(shiftKey || ctrlKey)) {
      event.preventDefault();
      let row = trRow.nextElementSibling;

      while (row && !row.classList.contains(TABLE_BODY_ROW_CLASS)) {
        row = row.nextElementSibling;
      }

      focusCellId = row && row.children[target.cellIndex].id;
    } else if (key === LEFT) {
      event.preventDefault();
      focusCellId = target.previousElementSibling && target.previousElementSibling.id;
    } else if (key === RIGHT) {
      event.preventDefault();
      focusCellId = target.nextElementSibling && target.nextElementSibling.id;
    }

    focusCellId && updateFocusCell(focusCellId);
  };

  return _jsxs("div", {
    css: tableRoot === null || tableRoot === void 0 ? void 0 : tableRoot.tableWrapperOverflow,
    children: [_jsx(TableARIAChangeAnnouncer, {
      liveText: liveText
    }), _jsx("span", {
      id: selectDescriptionID,
      className: "wf-u-visually-hidden",
      children: selectDescription
    }), _jsxs("table", _objectSpread(_objectSpread({
      css: tableRoot,
      className: className,
      summary: summary,
      ref: tableNodeRef || internalRef
    }, otherProps), {}, {
      children: [_jsx(TableCaption, {
        ariaCaption: ariaCaption,
        caption: caption,
        css: tableRoot === null || tableRoot === void 0 ? void 0 : tableRoot.captionTag
      }), _jsx(THead, _objectSpread(_objectSpread({}, headerComponentProps), {}, {
        className: "wf-table__columns",
        children: _jsx("tr", {
          children: columns.map((_ref2, index) => {
            let {
              key
            } = _ref2,
                column = _objectWithoutProperties(_ref2, _excluded2);

            const headerCellAlignment = [tableRoot.header, !nonStickyHeader && tableRoot.stickyHeader, column.align && tableRoot[`headerAlign${column.align}`], column.noWrap && tableRoot.noWrapCell];
            return _createElement(TableHeaderCell, _objectSpread(_objectSpread({
              css: headerCellAlignment
            }, column), {}, {
              key: key,
              columnKey: key,
              index: index
            }));
          })
        })
      })), _jsx(TBody, _objectSpread(_objectSpread({}, bodyComponentProps), {}, {
        className: "wf-table__data",
        children: tableData.length > 0 ? tableData.map((item, itemIndex) => {
          const itemKey = getRowKey(item);

          if (process.env.NODE_ENV !== "production" && typeof itemKey === "undefined") {
            if (typeof rowKey === "string") {
              throw Error(`Expected data provided to <Table rowKey=${rowKey} ... /> at row index ${itemIndex} to return a value.\nThe expression 'data[${itemIndex}].${rowKey}' returned undefined.\nCheck the items passed to the data property to ensure each item provides a unique value for ${rowKey}.\nItem:  ${JSON.stringify(item, null, 2 // eslint-disable-line no-magic-numbers
              )}`);
            } else {
              throw Error(`Expected data provided to <Table rowKey=rowKeyFunction ... /> at row index ${itemIndex} to return a value.\nThe expression 'rowKeyFunction(data[${itemIndex}])' returned undefined.\nCheck the items passed to the data property to ensure the function provided by rowKey returns a unique value for each item.\nItem: ${JSON.stringify(item, null, 2 // eslint-disable-line no-magic-numbers
              )}`);
            }
          }

          const trProps = _objectSpread(_objectSpread({}, resultOf(bodyRowComponentProps, {
            item,
            itemIndex,
            itemKey
          })), {}, {
            className: clsx(TABLE_BODY_ROW_CLASS, resultOf(rowClassName, {
              item,
              itemIndex,
              itemKey
            }))
          });

          if (typeof TBodyRow === "function") {
            trProps.item = item;
            trProps.itemKey = itemKey;
            trProps.columns = columns;
            trProps.itemIndex = itemIndex;
          }

          if (selectedRows && selectableTypeRow) {
            trProps["aria-selected"] = selectedRows.indexOf(itemKey) >= 0;
          }

          return _createElement(TBodyRow, _objectSpread(_objectSpread({}, trProps), {}, {
            [ROW_KEY_ATTRIBUTE_NAME]: itemKey,
            key: itemKey
          }), columns.map((column, columnIndex) => {
            const tableBodyCellProps = getTableBodyCellProps(selectableTypeRow, columnIndex, itemIndex);
            const cellContent = item[column.key];
            const cellData = typeof cellContent === "undefined" && column.emptyValue ? resultOf(column.emptyValue, item) : cellContent;
            const cellElementProp = cellData === null || cellData === void 0 ? void 0 : cellData.props;
            const cellAlignMent = column.align ? tableRoot[`cellAlign${column.align}`] : typeof cellData === "number" && tableRoot.cellAlignright;
            const cellStyling = [tableRoot.cell, cellAlignMent, (cellElementProp === null || cellElementProp === void 0 ? void 0 : cellElementProp.type) === "button" && tableRoot.buttonCell, column.noWrap && tableRoot.noWrapCell];
            return _jsx(TableBodyCell, _objectSpread({
              css: cellStyling,
              className: column.className,
              columnKey: column.key,
              columnIndex: columnIndex,
              column: column,
              item: item,
              itemKey: itemKey,
              itemIndex: itemIndex,
              onKeyDown: selectableTypeRow ? handleKeyDown : null,
              onClick: selectableTypeRow ? handleClick : null,
              isSelected: selectedRows && selectedRows.indexOf(itemKey) >= 0
            }, tableBodyCellProps), column.key);
          }));
        }) : _jsx("tr", {
          children: _jsx("td", {
            css: tableRoot.emptyDataMessageContent,
            className: "wf-table__empty-data-message-content",
            colSpan: columns.length,
            children: _jsx(EmptyState, {
              header: (emptyDataMessage === null || emptyDataMessage === void 0 ? void 0 : emptyDataMessage.title) || "",
              message: typeof emptyDataMessage === "string" ? emptyDataMessage : emptyDataMessage.body,
              alignContent: "center"
            })
          })
        })
      })), footerRows && footerRows.length > 0 && _jsx(TFooter, _objectSpread(_objectSpread({}, footerComponentProps), {}, {
        className: "wf-table-footer",
        children: footerRows.map(row => {
          if (row && row.footerColumns) {
            return _jsx(TFooterRow, _objectSpread(_objectSpread({
              className: "wf-table-footer__row"
            }, footerRowComponentProps), {}, {
              css: tableRoot.footerRow,
              children: row.footerColumns.map((column, columnIndex) => _jsx(TableFooterCell, {
                css: tableRoot.cell,
                className: "wf-table__cell",
                columnKey: column.key,
                columnIndex: columnIndex,
                column: column,
                heading: row.heading,
                data: tableData
              }, column.key))
            }), "topRow");
          } else {
            var _tableRoot$footerRow;

            return _jsx("tr", {
              className: "wf-table-footer__bottomRow",
              css: (_tableRoot$footerRow = tableRoot.footerRow) === null || _tableRoot$footerRow === void 0 ? void 0 : _tableRoot$footerRow.bottomRow,
              children: _jsx("td", {
                colSpan: columns.length,
                children: row
              })
            }, "bottomRow");
          }
        })
      }))]
    }))]
  });
});
BaseTable.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The columns configuration for the table, which contains the columns to
   * be rendered by the component.
   *
   * This attribute takes an array of objects. Each object is considered the
   * column's configuration and dictates how the header and body cell for that
   * column should be rendered and behave
   */
  columns: PropTypes.arrayOf(PropTypes.shape({
    /** The key to locate the value for the column inside each record */
    key: PropTypes.string.isRequired,

    /**
     * The display label for the header or column information.
     * Overridden by `columns.header` prop.
     */
    label: PropTypes.string,

    /**
     * The header content for the column.
     * Overrides `columns.label` prop.
     * One of type: `string`, `func`.
     */
    header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /**
     * The alignment of column's cells. Can be `left`, `center` or `right`.
     * Defaults to `left`
     */
    align: PropTypes.oneOf(["left", "center", "right"]),

    /** Additional class name added to the header and body cells */
    className: PropTypes.string,

    /** Describes whether the text content should not wrap. Default to `false` */
    noWrap: PropTypes.bool,

    /** Additional styles for the header cell */
    style: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types

    /** The content of the cell to use when the record value is empty */
    emptyValue: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /**
     * A function that will return a formatted version of the columns data.
     * It will receive one object argument with `cellContent`, `item`, and `selected`.
     */
    formatter: PropTypes.func,

    /** The Component that renders the header cell */
    headerCellComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /** The extra props passed to the headerCellComponent */
    headerCellComponentProps: PropTypes.object,
    // eslint-disable-line react/forbid-prop-types

    /** The Component that renders the body cell */
    bodyCellComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

    /** The extra props passed to the bodyCellComponent */
    bodyCellComponentProps: PropTypes.object // eslint-disable-line react/forbid-prop-types

  })).isRequired,

  /**
   * A function that generates a unique key for each items,
   * or a string that identifies the unique key among the items.
   * For more information, check https://facebook.github.io/react/docs/lists-and-keys.html#keys
   */
  rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,

  /**
   * Provides the option to enhance the Table caption by adding an additional accessible announcement,
   * for Assistive Technology, to the Table caption. It can also stand alone, without a Table caption,
   * offering Assistive Technology further information about the Table.
   */
  ariaCaption: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  autoWidth: PropTypes.bool,

  /** The Component that renders the body */
  bodyComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** The extra props passed to the bodyComponent */
  bodyComponentProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** The Component that renders the tr inside the body */
  bodyRowComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /**
   * The extra props passed to the bodyRowComponent or a function that will be called by each row,
   * to get one or more object properties, which will be appended to the row. If it's a function, it will
   * be called with the following arguments:
   * `fn({
   *    item, // <= row data
   *    itemIndex, // <= row index
   *    itemKey // <= value returned by rowKey
   *  })`
   */
  bodyRowComponentProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),

  /** The caption content for the `<caption>` element */
  caption: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,

  /**
   * Override for strings used within the Selectable HOC component
   */
  customStrings: PropTypes.shape({
    emptyDataMessage: PropTypes.oneOfType([
    /**
     * Acceptance of a string is for backward compatibility only.
     */
    PropTypes.string, PropTypes.shape({
      /**
       * Title used for EmptyState when no data is visible.
       * Defaults to "No data available."
       */
      title: PropTypes.string,

      /**
       * Body for EmptyState when no Table data is visible.
       * Defaults to "".
       */
      body: PropTypes.string
    })])
  }),

  /**
   * The data to be consumed by the table. This attribute takes an array of objects,
   * which are considered the records to display
   */
  data: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /** The Component that renders the footer. */
  footerComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** The extra props passed to the footerComponent. */
  footerComponentProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** The Component that renders the tr inside the body. */
  footerRowComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** The footerRows contains the footer topRow and bottomRow data. */
  footerRows: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /** The Component that renders the header */
  headerComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** The extra props passed to the headerComponent */
  headerComponentProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** To set max height for the table */
  maxHeight: PropTypes.string,

  /** If set to `true`, the header won't be sticky. */
  nonStickyHeader: PropTypes.bool,
  onCollapseColumns: PropTypes.func,

  /**
   * Applicable only in pagination scenarios.
   * When both paginatedData and data are present, paginatedData takes precedence.
   * It is used only to render table rows in pagination scenarios.
   */
  paginatedData: PropTypes.arrayOf(PropTypes.object),
  // eslint-disable-line react/forbid-prop-types

  /**
   * A String, or Function that returns a String, representing the class or space-separated classes to be
   * appended to the row's class attribute.
   *
   * If a Function, it will receive a single argument, an Object literal as described below:
   *
   * @param {Object} row
   * @param {String} row.item The item data supplied to this row.
   * @param {Number} row.itemIndex The row index.
   * @param {String} row.itemKey The unique id of the row, as defined by `props.rowKey`
   */
  rowClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** Selected Rows data. */
  selectedRows: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** The `summary` attribute of the `<table>` element */
  summary: PropTypes.string
} : {};
BaseTable.defaultProps = {
  children: null,
  className: null,
  autoWidth: false,
  summary: null,
  ariaCaption: null,
  caption: null,
  rowClassName: null,
  data: [],
  headerComponent: "thead",
  headerComponentProps: null,
  bodyComponent: "tbody",
  bodyComponentProps: null,
  bodyRowComponent: "tr",
  bodyRowComponentProps: null,
  footerComponent: "tfoot",
  footerRowComponent: "tr",
  footerComponentProps: null,
  maxHeight: null,
  onCollapseColumns: null,
  customStrings: {},
  paginatedData: [],
  footerRows: [],
  selectedRows: [],
  nonStickyHeader: false
};
BaseTable.displayName = "BaseTable";
export default BaseTable;